import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { activityLogAPI } from '../services/api';

/* ── Action → Türkçe & renk ── */
const ACTION_META = {
  create: { label: 'Oluşturdu', color: '#059669', icon: '➕' },
  update: { label: 'Güncelledi', color: '#2563eb', icon: '✏️' },
  delete: { label: 'Sildi', color: '#dc2626', icon: '🗑️' },
  import: { label: 'İmport Etti', color: '#7c3aed', icon: '📥' },
  approve_draft: { label: 'Onayladı', color: '#059669', icon: '✅' },
  use_night: { label: 'Kullandı', color: '#d97706', icon: '🌙' },
};

/* ── Entity → Türkçe ── */
const ENTITY_LABELS = {
  customer: 'Yolcu', ticket: 'Bilet', payment: 'Ödeme', hotel_option: 'Opsiyon',
  free_night_credit: 'Ü.Gece', free_night: 'Ü.Gece', checkin: 'Check-in',
  card_transaction: 'Kart İşlemi', visa: 'Vize', visa_system: 'Vize Sistemi',
  visa_requirement: 'Vize Evrak', visa_appointment: 'Vize Randevu',
  offer: 'Teklif', hr: 'İK', reservation: 'Rezervasyon',
  mailing: 'Mailing', bulletin: 'Bülten', announcement: 'Duyuru',
  expense: 'Gider', flight_tracking: 'Uçuş Takip', flight_search: 'Uçuş Arama',
  weekly_duty: 'Nöbet', kredi: 'Kredi', company_perf: 'Firma Performans',
  payment_perf: 'Ödeme Performans', finance: 'Finans', portal: 'Portal',
  user: 'Kullanıcı', content: 'İçerik', mio_sync: 'MIO Sync',
  company: 'Firma', hotel_rate: 'Otel Fiyat', marketing: 'Pazarlama',
  calendar: 'Takvim', auth: 'Oturum', customer_draft: 'Yolcu Taslak',
  notification: 'Bildirim', passport_reader: 'Pasaport', mail_tracking: 'Mail Takip',
};

/* ── Entity → renk ── */
const ENTITY_COLORS = {
  ticket: '#0284c7', customer: '#4f46e5', payment: '#059669', checkin: '#d97706',
  reservation: '#7c3aed', visa: '#ec4899', hotel_option: '#f59e0b', offer: '#f97316',
  company: '#16a34a', marketing: '#ea580c', hr: '#a855f7', mailing: '#06b6d4',
  expense: '#dc2626', kredi: '#8b5cf6', flight_tracking: '#0ea5e9',
};

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diffSec = Math.floor((now - d) / 1000);
  if (diffSec < 60) return 'az önce';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}dk`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}sa`;
  return `${Math.floor(diffSec / 86400)}g`;
}

const ActivityFeed = ({ maxItems = 20 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const socketRef = useRef(null);
  const feedRef = useRef(null);

  // Initial fetch
  const fetchRecent = useCallback(async () => {
    setError(false);
    try {
      const res = await activityLogAPI.getRecent({ limit: maxItems });
      setItems(res.data || []);
    } catch { setError(true); } // V71 — hata sessizce "aktivite yok"a dönmesin
    setLoading(false);
  }, [maxItems]);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  // WebSocket — V71: admin tüm akışı (activity_feed), diğerleri yalnız kendi kayıtlarını (user:<id>) alır
  useEffect(() => {
    const token = localStorage.getItem('mtu_token');
    if (!token) return;

    // Production: same origin (socket.io default), Dev: explicit URL
    const socket = io(window.location.origin, {
      auth: { token },
      transports: ['polling'],
      upgrade: false, // Railway WS upgrade fail
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 5000,
      reconnectionDelayMax: 30000,
    });
    socketRef.current = socket;

    socket.on('activity', (activity) => {
      setItems(prev => {
        const next = [activity, ...prev];
        return next.slice(0, maxItems);
      });
    });
    socket.on('connect_error', (err) => {
      console.warn('[ActivityFeed] WS bağlantı hatası:', err.message);
    });

    return () => { socket.disconnect(); };
  }, [maxItems]);

  if (loading) return <div className="loading-state" style={{ padding: 16 }}>Yükleniyor…</div>;
  if (error && !items.length) return (
    <div className="error-state" style={{ margin: 12 }}>
      Aktivite akışı yüklenemedi
      <button className="btn btn-secondary btn-xs" onClick={fetchRecent}>Tekrar dene</button>
    </div>
  );
  if (!items.length) return <div className="empty-text">Henüz aktivite yok</div>;

  return (
    <div ref={feedRef} className="activity-feed" style={{ maxHeight: 420, overflowY: 'auto' }}>
      {items.map((item, idx) => {
        const meta = ACTION_META[item.action] || { label: item.action, color: '#64748b', icon: '📋' };
        const entityLabel = ENTITY_LABELS[item.entity_type] || item.entity_type;
        const entityColor = ENTITY_COLORS[item.entity_type] || '#64748b';
        const userName = item.full_name || item.username || '?';

        return (
          <div key={item.id || idx} className="activity-feed-item" style={{
            animation: idx === 0 ? 'actFadeIn .4s ease' : 'none',
          }}>
            {/* Avatar */}
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: `color-mix(in srgb, ${entityColor} 14%, transparent)`, color: entityColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700,
            }}>
              {userName.charAt(0).toUpperCase()}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--fg-primary)' }}>{userName}</strong>
                {' '}
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                  background: `color-mix(in srgb, ${meta.color} 12%, transparent)`, color: meta.color,
                }}>{meta.icon} {meta.label}</span>
                {' '}
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                  background: `color-mix(in srgb, ${entityColor} 12%, transparent)`, color: entityColor,
                }}>{entityLabel}</span>
              </div>
              {item.details && (
                <div style={{ fontSize: 12, color: 'var(--fg-secondary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.details}
                </div>
              )}
            </div>

            {/* Time */}
            <div style={{ fontSize: 11, color: 'var(--fg-tertiary)', whiteSpace: 'nowrap', flexShrink: 0, paddingTop: 2 }}>
              {timeAgo(item.created_at)}
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes actFadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .activity-feed::-webkit-scrollbar { width: 4px; }
        .activity-feed::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: 4px; }
        .activity-feed-item { display: flex; gap: 10px; padding: 8px 12px; border-bottom: 1px solid var(--border-subtle); transition: background var(--duration-fast, .15s); }
        .activity-feed-item:hover { background: var(--bg-surface-2); }
      `}</style>
    </div>
  );
};

export default ActivityFeed;
